import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { sendBulkProcessCommands } from '../../supportBulkCommand';

const STATUS_LABELS = {
  pending: 'Aguardando',
  connecting: 'Conectando…',
  success: 'Enviado',
  error: 'Erro',
  cancelled: 'Cancelado',
};

const BulkCommandModal = ({ show, onHide, equipments, currentTheme }) => {
  const user = JSON.parse(localStorage.getItem('userData'));
  const abortRef = useRef(null);

  const [command, setCommand] = useState('');
  const [filter, setFilter] = useState('');
  const [selectedSerials, setSelectedSerials] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [progressMap, setProgressMap] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);

  const isDark = currentTheme === 'dark';

  const filteredEquipments = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return equipments || [];

    return (equipments || []).filter((item) => {
      const serial = item?.serialnumber?.toLowerCase() || '';
      const name = item?.nameEquipament?.toLowerCase() || '';
      const company = item?.company?.name?.toLowerCase() || '';
      return serial.includes(term) || name.includes(term) || company.includes(term);
    });
  }, [equipments, filter]);

  const allFilteredSelected = useMemo(() => {
    if (!filteredEquipments.length) return false;
    return filteredEquipments.every((item) => selectedSerials.has(item.serialnumber));
  }, [filteredEquipments, selectedSerials]);

  const selectedCount = selectedSerials.size;

  const resetState = () => {
    setCommand('');
    setFilter('');
    setSelectedSerials(new Set());
    setSending(false);
    setErrorMessage('');
    setProgressMap({});
    setShowConfirm(false);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [show]);

  const handleClose = () => {
    if (sending && abortRef.current) {
      abortRef.current.abort();
    }
    onHide();
  };

  const toggleSerial = (serial, checked) => {
    setSelectedSerials((prev) => {
      const next = new Set(prev);
      if (checked) next.add(serial);
      else next.delete(serial);
      return next;
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedSerials((prev) => {
      const next = new Set(prev);
      filteredEquipments.forEach((item) => {
        if (checked) next.add(item.serialnumber);
        else next.delete(item.serialnumber);
      });
      return next;
    });
  };

  const handlePrepareSend = () => {
    setErrorMessage('');

    if (!command.trim()) {
      setErrorMessage('Informe o comando a ser enviado.');
      return;
    }

    if (!selectedCount) {
      setErrorMessage('Selecione ao menos um equipamento.');
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    const serials = Array.from(selectedSerials);
    const initialProgress = serials.reduce((acc, serial) => {
      acc[serial] = { status: 'pending' };
      return acc;
    }, {});

    setProgressMap(initialProgress);
    setShowConfirm(false);
    setSending(true);
    setErrorMessage('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await sendBulkProcessCommands({
        serials,
        command,
        user,
        signal: controller.signal,
        onProgress: ({ serial, status, message }) => {
          setProgressMap((prev) => ({
            ...prev,
            [serial]: { status, message },
          }));
        },
      });
    } catch (err) {
      setErrorMessage(err?.message || 'Falha ao enviar comandos em massa.');
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const successCount = Object.values(progressMap).filter((p) => p.status === 'success').length;
  const errorCount = Object.values(progressMap).filter((p) => p.status === 'error').length;
  const hasResults = Object.keys(progressMap).length > 0;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop={sending ? 'static' : true}
      contentClassName={isDark ? 'bg-dark text-light border-secondary' : 'bg-white'}
    >
      <Modal.Header
        closeButton={!sending}
        className={isDark ? 'bg-dark text-light border-secondary' : undefined}
      >
        <Modal.Title className="fw-semibold" style={{ fontSize: '1rem' }}>
          Comando em massa
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={isDark ? 'bg-dark text-light' : undefined}>
        {showConfirm ? (
          <>
            <p className="mb-2">
              O comando abaixo será enviado para <strong>{selectedCount}</strong> equipamento(s):
            </p>
            <pre
              className="p-2 rounded mb-3"
              style={{
                background: isDark ? '#2b3035' : '#f8f9fa',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.85rem',
              }}
            >
              {command.trim()}
            </pre>
            <Alert variant="warning" className="mb-0">
              Esta ação executa um comando remoto nos equipamentos selecionados. Confirme apenas se tiver certeza.
            </Alert>
          </>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Comando</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                placeholder="Ex.: ls -la /home/orangepi"
                disabled={sending}
              />
              <Form.Text className={isDark ? 'text-secondary' : 'text-muted'}>
                Mesmo formato do terminal na sessão de suporte individual (comando shell).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Filtrar equipamentos</Form.Label>
              <Form.Control
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Serial, nome ou empresa…"
                disabled={sending}
              />
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Check
                type="checkbox"
                id="bulk-select-all-filtered"
                label="Selecionar todos (lista filtrada)"
                checked={allFilteredSelected}
                onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                disabled={sending || !filteredEquipments.length}
              />
              <Badge bg="secondary">{selectedCount} selecionado(s)</Badge>
            </div>

            <div
              className="border rounded p-2"
              style={{
                maxHeight: 280,
                overflowY: 'auto',
                borderColor: isDark ? 'rgba(255,255,255,0.15)' : undefined,
              }}
            >
              {filteredEquipments.length === 0 ? (
                <p className="text-muted mb-0 small">Nenhum equipamento encontrado.</p>
              ) : (
                filteredEquipments.map((item) => {
                  const serial = item.serialnumber;
                  const progress = progressMap[serial];
                  return (
                    <div
                      key={item._id || serial}
                      className="d-flex align-items-start gap-2 py-1 border-bottom"
                      style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : undefined }}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`bulk-eq-${serial}`}
                        checked={selectedSerials.has(serial)}
                        onChange={(e) => toggleSerial(serial, e.target.checked)}
                        disabled={sending}
                        className="mt-1"
                      />
                      <label htmlFor={`bulk-eq-${serial}`} className="flex-grow-1 mb-0" style={{ cursor: 'pointer' }}>
                        <div className="fw-semibold small">{serial}</div>
                        <div className={`small ${isDark ? 'text-secondary' : 'text-muted'}`}>
                          {item?.company?.name || '—'}
                          {item?.versaosw ? ` · SW ${item.versaosw}` : ''}
                        </div>
                        {progress && (
                          <div className="small mt-1">
                            <span
                              className={
                                progress.status === 'success'
                                  ? 'text-success'
                                  : progress.status === 'error'
                                    ? 'text-danger'
                                    : 'text-muted'
                              }
                            >
                              {STATUS_LABELS[progress.status] || progress.status}
                              {progress.message ? `: ${progress.message}` : ''}
                            </span>
                          </div>
                        )}
                      </label>
                    </div>
                  );
                })
              )}
            </div>

            {errorMessage && (
              <Alert variant="danger" className="mt-3 mb-0">
                {errorMessage}
              </Alert>
            )}

            {hasResults && !sending && (
              <Alert variant={errorCount ? 'warning' : 'success'} className="mt-3 mb-0">
                Concluído: {successCount} enviado(s), {errorCount} com erro.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className={isDark ? 'bg-dark text-light border-secondary' : undefined}>
        {showConfirm ? (
          <>
            <Button variant="outline-secondary" onClick={() => setShowConfirm(false)} disabled={sending}>
              Voltar
            </Button>
            <Button
              onClick={handleConfirmSend}
              disabled={sending}
              style={{
                backgroundColor: 'var(--brand-light-green, #8fad15)',
                color: '#fff',
                border: 'none',
              }}
            >
              Confirmar envio
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={handleClose} disabled={sending}>
              {hasResults && !sending ? 'Fechar' : 'Cancelar'}
            </Button>
            <Button
              onClick={handlePrepareSend}
              disabled={sending || !selectedCount || !command.trim()}
              style={{
                backgroundColor: 'var(--brand-light-green, #8fad15)',
                color: '#fff',
                border: 'none',
              }}
            >
              {sending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enviando…
                </>
              ) : (
                'Enviar comando'
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkCommandModal;
